import { GameWs } from '@/utils/gameWs';
import { getWsBase, getOrCreateUserId } from '@/utils/session';
import { getStoredAuthToken } from '@/utils/userApi';
class WsManager {
    ws = null;
    handler = null;
    queue = [];
    language;
    sessionId;
    lastParams = null;
    reconnectAttempts = 0;
    maxReconnectAttempts = 1;
    reconnectTimer = null;
    status = { state: 'closed', attempts: 0 };
    statusListeners = new Set();
    shouldReconnect = false;
    modelCode;
    // idle disconnect removed: keep connection while overlay is open
    setHandler(handler) {
        this.handler = handler;
        if (handler && this.queue.length) {
            const pending = [...this.queue];
            this.queue = [];
            pending.forEach((action) => {
                try {
                    handler(action);
                }
                catch (e) {
                    console.error('Handler error', e);
                }
            });
        }
    }
    connect(params) {
        const baseUrl = (params.baseUrl || getWsBase()).replace(/\/$/, '');
        const userId = params.userId || getOrCreateUserId();
        const token = getStoredAuthToken() || undefined;
        if (params.language !== undefined)
            this.language = params.language;
        // Always set sessionId; allow undefined to explicitly clear stale id for new sessions
        this.sessionId = params.sessionId;
        if (params.modelCode !== undefined)
            this.modelCode = params.modelCode;
        // Persist the effective values so future retries don't wipe sessionId/language
        this.lastParams = {
            scriptId: params.scriptId,
            language: this.language,
            sessionId: this.sessionId,
            modelCode: this.modelCode,
            shareToken: params.shareToken,
            baseUrl,
            userId,
        };
        this.shouldReconnect = true;
        // Close previous connection if exists (tag as replace to distinguish in logs)
        if (this.ws) {
            // Temporarily disable reconnect to avoid scheduling from stale socket's onClose
            const prev = this.ws;
            this.shouldReconnect = false;
            try {
                prev.close?.('replace', true);
            }
            catch { }
            this.ws = null;
        }
        this.setStatus({ state: this.reconnectAttempts > 0 ? 'reconnecting' : 'connecting', attempts: this.reconnectAttempts });
        const ws = new GameWs({ baseUrl, userId, token });
        try {
            console.info('[WSM] connect', { scriptId: params.scriptId, language: this.language, sessionId: this.sessionId, modelName: this.modelCode, baseUrl, userId });
        }
        catch { }
        ws.connect((action) => {
            if (this.handler) {
                try {
                    this.handler(action);
                }
                catch (e) {
                    console.error('Handler error', e);
                }
            }
            else {
                this.queue.push(action);
            }
        }, {
            startScriptId: params.scriptId,
            language: this.language,
            sessionId: this.sessionId,
            model: this.modelCode,
            shareToken: params.shareToken,
            onOpen: () => {
                this.clearReconnectTimer();
                this.reconnectAttempts = 0;
                this.setStatus({ state: 'open', attempts: 0 });
                try {
                    console.info('[WSM] open');
                }
                catch { }
                // Re-enable reconnect only for the active socket
                this.shouldReconnect = true;
            },
            onClose: () => {
                // Ignore closes from stale sockets (from previous connects)
                if (this.ws && this.ws !== ws)
                    return;
                if (!this.shouldReconnect)
                    return;
                this.scheduleReconnect();
            },
            onError: () => {
                // wait for onClose to schedule reconnect; no-op
            },
        });
        this.ws = ws;
    }
    setLanguage(language) {
        this.language = language;
        if (this.lastParams) {
            this.lastParams.language = language;
        }
    }
    sendText(text, history, globals) {
        if (!this.ws)
            throw new Error('WebSocket not connected');
        this.ws.sendText(text, this.language, this.sessionId, this.modelCode, history, globals);
    }
    async waitUntilOpen() {
        if (!this.ws)
            throw new Error('WebSocket not connected');
        await this.ws.waitUntilOpen();
    }
    async deleteSession(sessionId) {
        if (!this.ws)
            throw new Error('WebSocket not connected');
        await this.ws.waitUntilOpen();
        const sid = sessionId || this.sessionId;
        if (!sid)
            throw new Error('No session_id available to delete');
        this.ws.send({ type: 'delete_session', session_id: sid });
    }
    async sendActionLazy(args) {
        const { scriptId, language, sessionId, action } = args;
        if (language)
            this.language = language;
        if (sessionId !== undefined)
            this.sessionId = sessionId;
        if (!this.ws) {
            this.connect({ scriptId, language: this.language, sessionId: this.sessionId, modelCode: this.modelCode, shareToken: this.lastParams?.shareToken });
            try {
                await this.waitUntilOpen();
            }
            catch { }
        }
        else if (this.ws.isOpen && this.ws.isOpen()) {
            const needReconnect = !this.lastParams || this.lastParams.scriptId !== scriptId || this.lastParams.sessionId !== this.sessionId || this.lastParams.language !== this.language;
            if (needReconnect) {
                this.connect({ scriptId, language: this.language, sessionId: this.sessionId, modelCode: this.modelCode, shareToken: this.lastParams?.shareToken });
                try {
                    await this.waitUntilOpen();
                }
                catch { }
            }
        }
        else {
            try {
                await this.waitUntilOpen();
            }
            catch { }
        }
        try {
            this.ws.send(action);
        }
        catch (e) {
            console.error('WS sendAction error', e);
            throw e;
        }
    }
    setSessionId(sessionId) {
        this.sessionId = sessionId;
        if (this.lastParams) {
            this.lastParams.sessionId = sessionId;
        }
    }
    setModelCode(modelCode) {
        this.modelCode = modelCode;
        if (this.lastParams) {
            this.lastParams.modelCode = modelCode;
        }
    }
    close() {
        this.shouldReconnect = false;
        try {
            this.ws?.close?.('manual', true);
        }
        catch { }
        this.ws = null;
        this.clearReconnectTimer();
        this.reconnectAttempts = 0;
        this.setStatus({ state: 'closed', attempts: 0 });
        try {
            console.info('[WSM] closed manually');
        }
        catch { }
    }
    get() {
        return this.ws;
    }
    // Auto-reconnect helpers
    scheduleReconnect() {
        if (!this.lastParams)
            return;
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            this.setStatus({ state: 'closed', attempts: this.reconnectAttempts });
            return;
        }
        this.reconnectAttempts += 1;
        const delay = [1500, 3000, 5000, 8000, 10000][Math.min(this.reconnectAttempts - 1, 4)];
        this.setStatus({ state: 'reconnecting', attempts: this.reconnectAttempts });
        this.clearReconnectTimer();
        try {
            console.warn('[WSM] scheduleReconnect', { attempt: this.reconnectAttempts, delay });
        }
        catch { }
        this.reconnectTimer = window.setTimeout(() => {
            if (!this.lastParams)
                return;
            // Always use the most recent language/sessionId/modelCode when reconnecting
            this.connect({ ...this.lastParams, language: this.language, sessionId: this.sessionId, modelCode: this.modelCode, shareToken: this.lastParams?.shareToken });
        }, delay);
    }
    clearReconnectTimer() {
        if (this.reconnectTimer) {
            try {
                window.clearTimeout(this.reconnectTimer);
            }
            catch { }
            this.reconnectTimer = null;
        }
    }
    retryNow() {
        if (!this.lastParams)
            return;
        this.clearReconnectTimer();
        this.reconnectAttempts = 0;
        this.setStatus({ state: 'connecting', attempts: 0 });
        this.connect({ ...this.lastParams, language: this.language, sessionId: this.sessionId, modelCode: this.modelCode, shareToken: this.lastParams?.shareToken });
    }
    onStatus(listener) {
        this.statusListeners.add(listener);
        // Push current status immediately
        try {
            listener(this.status);
        }
        catch { }
        return () => { this.statusListeners.delete(listener); };
    }
    setStatus(s) {
        this.status = s;
        for (const l of this.statusListeners) {
            try {
                l(s);
            }
            catch { }
        }
        try {
            console.info('[WSM] status', s);
        }
        catch { }
    }
    // idle timer removed
    async sendTextLazy(args) {
        const { scriptId, language, sessionId, text, history, globals } = args;
        // Keep desired language/sessionId for the connection
        if (language)
            this.language = language;
        // Only update sessionId when caller provides a value; avoid clearing a valid sessionId
        if (sessionId !== undefined)
            this.sessionId = sessionId;
        if (!this.ws) {
            // connect lazily and wait until open; start_session is sent in GameWs.onopen
            this.connect({ scriptId, language: this.language, sessionId: this.sessionId, modelCode: this.modelCode });
            try {
                await this.waitUntilOpen();
            }
            catch { }
        }
        else if (this.ws.isOpen && this.ws.isOpen()) {
            // already open; ensure it's for the same script/session/language, else reconnect
            const needReconnect = !this.lastParams || this.lastParams.scriptId !== scriptId || this.lastParams.sessionId !== this.sessionId || this.lastParams.language !== this.language;
            if (needReconnect) {
                try {
                    console.warn('[WSM] reconnect on sendTextLazy due to mismatch', {
                        last: this.lastParams,
                        current: { scriptId, sessionId: this.sessionId, language: this.language },
                    });
                }
                catch { }
                this.connect({ scriptId, language: this.language, sessionId: this.sessionId, modelCode: this.modelCode });
                try {
                    await this.waitUntilOpen();
                }
                catch { }
            }
        }
        else {
            // connecting: just wait
            try {
                await this.waitUntilOpen();
            }
            catch { }
        }
        // Ensure the server session is started before sending the first text
        try {
            const wsAny = this.ws;
            if (wsAny && typeof wsAny.waitUntilSessionReady === 'function') {
                await wsAny.waitUntilSessionReady();
            }
        }
        catch { }
        // send user text now
        this.sendText(text, history, globals);
    }
}
export const wsManager = new WsManager();
